import { useMemo, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { todayCivilDate } from '~/models/civilDate'
import type { VoltageClass } from '~/models/voltageBreakdown'
import { useGetContractTrend } from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import {
  type ContractTrend,
  EMPTY_CONTRACT_TREND,
  VOLTAGES,
  fiscalYearOf,
  fiscalYearRange,
  toContractTrend,
} from '~/pages/CustomerInfo/models/contractTrend'

export type ContractTrendState = {
  /** 選べる年度 (今年度の 2 年前から 10 年先まで)。 */
  fiscalYears: number[]
  fyFrom: number
  fyTo: number
  voltages: ReadonlySet<VoltageClass>
  setFyFrom: (fy: number) => void
  setFyTo: (fy: number) => void
  toggleVoltage: (voltage: VoltageClass) => void
  /** 電圧階級の表示を既定 (全部表示) に戻す。 */
  resetVoltages: () => void
  trend: ContractTrend
  isLoading: boolean
  isError: boolean
}

/**
 * 契約推移タブの状態: 年度範囲 (既定 = 今年度)・電圧階級の表示・集計結果。
 * 日ごとの集計は backend (GetContractTrend。 エリア・年度範囲はその条件) が計算し、 ここは表示用に並べる。
 */
export const useContractTrend = (
  areaCode: AreaCode | undefined,
): ContractTrendState => {
  const currentFy = fiscalYearOf(todayCivilDate())
  const fiscalYears = useMemo(
    () => Array.from({ length: 13 }, (_, i) => currentFy - 2 + i),
    [currentFy],
  )
  const [fyFrom, setFyFromState] = useState(currentFy)
  const [fyTo, setFyToState] = useState(currentFy)
  const [voltages, setVoltages] = useState<ReadonlySet<VoltageClass>>(
    new Set(VOLTAGES),
  )

  // 開始 > 終了にならないよう、 片方を動かしてもう片方を越えたら揃える
  const setFyFrom = (fy: number): void => {
    setFyFromState(fy)
    if (fy > fyTo) setFyToState(fy)
  }
  const setFyTo = (fy: number): void => {
    setFyToState(fy)
    if (fy < fyFrom) setFyFromState(fy)
  }
  const toggleVoltage = (voltage: VoltageClass): void => {
    const next = new Set(voltages)
    if (!next.delete(voltage)) next.add(voltage)
    setVoltages(next)
  }

  const resetVoltages = (): void => setVoltages(new Set(VOLTAGES))

  const { from, to } = fiscalYearRange(fyFrom, fyTo)
  const { data, isLoading, isError } = useGetContractTrend(areaCode, from, to)
  const trend = useMemo(
    () =>
      data === undefined
        ? EMPTY_CONTRACT_TREND
        : toContractTrend(data, voltages),
    [data, voltages],
  )

  return {
    fiscalYears,
    fyFrom,
    fyTo,
    voltages,
    setFyFrom,
    setFyTo,
    toggleVoltage,
    resetVoltages,
    trend,
    isLoading,
    isError,
  }
}
